import { IGroupListItemDef } from "@components/configurationList";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportRowDef, IReportVariantDef, ISmartReportTableRow } from "@components/smart/smartTable";
import { CurrencyCode } from "@odata/GeneratedEnums";
import { transformLabelsHierarchyFilter } from "@pages/reports/expensesAndRevenue/ExpensesAndRevenue.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { TRecordType } from "global.types";
import i18next from "i18next";
import { formatCurrency } from "types/Currency";

import { FieldType, ReportTableRowType, Sort } from "../../../enums";
import BindingContext from "../../../odata/BindingContext";
import { CommonReportProps, getDocumentNumberOursOverride, getDocumentTypeFilterDef } from "../CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs
} from "../customFilterComponents/ComposedDateRange";
import {
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportConfigGroup,
    TGetReportConfigListItemOverride,
    TimeAggregationFunction,
    TReportColumnOverrides,
    TReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";

export enum CbaExpensesAndRevenueVariant {
    Evala = "-186"
}

export const CbaExpensesAndRevenueProps = {
    labels: BindingContext.localContext("Labels")
};

const getCommonPlaceholderReportHierarchy = (): IReportHierarchy => ({
    "Aggregate": true,
    "Groups": [
        {
            "ColumnAlias": "CbaEntryType_Name"
        }
    ],
    "Columns": [
        {ColumnAlias: "CbaEntry_Date", AggregationFunction: TimeAggregationFunction.Day},
        {ColumnAlias: "CbaEntry_NumberOurs"},
        {ColumnAlias: "PairedDocument_NumberOurs"},
        {ColumnAlias: "EntityBusinessPartner_Name"},
        {ColumnAlias: "CbaEntry_Description"},
    ],
    "Aggregations": [
        {ColumnAlias: "CbaEntry_AmountTaxed", AggregationFunction: NumberAggregationFunction.Sum},
        {ColumnAlias: "CbaEntry_AmountNonTaxed", AggregationFunction: NumberAggregationFunction.Sum},
    ]
});

const REQUIRED_GROUPS_COLUMNS = new Set(["CbaEntryType_Name"]);
const REQUIRED_AGGREGATIONS = new Set(["CbaEntry_AmountTaxed", "CbaEntry_AmountNonTaxed"]);
const DEFAULT_COLUMNS = new Set([
    "CbaEntry_Date",
    "CbaEntry_NumberOurs",
    "PairedDocument_NumberOurs",
    "EntityBusinessPartner_Name",
    "CbaEntry_Description",
]);

export const cbaExpensesAndRevenueConfigListItemsOverride: TGetReportConfigListItemOverride = (item: IGroupListItemDef): TReportConfigListItemOverride => {
    const id = item.id as string;

    if(REQUIRED_GROUPS_COLUMNS.has(id)) {
        return { isRequired: true, isDisabled: true, areItemsReadOnly: true, group: ReportConfigGroup.Groups };
    }

    if (REQUIRED_AGGREGATIONS.has(id)) {
        return {
            isRequired: true,
            isDisabled: true,
            areItemsReadOnly: true,
            group: ReportConfigGroup.Aggregations,
        };
    }

    if (DEFAULT_COLUMNS.has(id)) {
        return { isCopyOnly: false };
    }

    return item;
};

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.CbaExpensesAndRevenue;
    const title = i18next.t("Reporting:CbaExpensesAndRevenue.Title");
    const path = "CbaExpensesAndRevenue";
    const initialSortBy = [
        { id: "CbaEntryType_Name", sort: Sort.Desc }
    ];
    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue,
                CbaExpensesAndRevenueProps.labels
            ],
            filterDefinition: {
                ...getComposedDateRangeFieldsDefs(tableId),
                [CbaExpensesAndRevenueProps.labels]: {
                    type: FieldType.LabelSelect,
                    label: i18next.t("Reporting:CbaExpensesAndRevenue.Labels"),
                    filter: {
                        transformFilterValue: transformLabelsHierarchyFilter
                    },
                    fieldSettings: {
                        oneItemPerHierarchy: false,
                        addEmptyItem: false,
                        addEmptyValuesForHierarchies: true,
                        combinedLabelId: true,
                        alwaysIncludeHierarchyName: true
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {
                [BindingContext.localContext("DocumentType_Name")]: getDocumentTypeFilterDef("CbaEntry_DocumentTypeCode")
            }
        }
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage);
    };

    const parameters: string[] = [
        CommonReportProps.dateRange,
        CommonReportProps.dateRangeCustomValue,
        CbaExpensesAndRevenueProps.labels
    ];
    const defaultReportHierarchy = getCommonPlaceholderReportHierarchy();

    const defaultReportVariants: TRecordType<IReportVariantDef> = {
        [CbaExpensesAndRevenueVariant.Evala]: {
            ReportHierarchy: defaultReportHierarchy
        }
    };

    const columnOverrides: TReportColumnOverrides = (columnId: string) => {
        const cbaAmountColumns = ["CbaEntry_AmountNonTaxed", "CbaEntry_AmountTaxed"];
        const matchedCbaColumn = cbaAmountColumns.find((col) => columnId.startsWith(col));
        if (matchedCbaColumn) {
            return {
                label: i18next.t(`Reporting:Columns.${matchedCbaColumn}`) as string,
                formatter: (val, args) => {
                    const isGrandTotalRow = args.entity?.GroupColumnAlias === "CbaEntryType_Name" && !args.entity?.["CbaEntryType_Name"];
                    const displayValue = isGrandTotalRow ? val as number : Math.abs(val as number);
                    const formattedValue = formatCurrency(displayValue, CurrencyCode.CzechKoruna);
                    return {
                        value: formattedValue,
                        tooltip: formattedValue
                    };
                }
            };
        }
        if (columnId === "CbaEntry_NumberOurs") {
            return getDocumentNumberOursOverride({
                documentTypeProperty: "CbaEntry_DocumentTypeCode",
                documentIdProperty: "CbaEntry_DocumentId"
            });
        }
        if (columnId === "PairedDocument_NumberOurs") {
            return getDocumentNumberOursOverride({
                documentTypeProperty: "PairedDocument_DocumentTypeCode",
                documentIdProperty: "PairedDocument_Id"
            });
        }
        return null;
    };

    const rowFormatter = (rowDef: IReportRowDef): Partial<ISmartReportTableRow> => {
        const isGrandTotal = rowDef.Type === ReportTableRowType.Total
            && rowDef.Value.GroupColumnAlias === "CbaEntryType_Name"
            && !rowDef.Value["CbaEntryType_Name"];
        if (isGrandTotal) {
            return {
                isHighlighted: true,
                isBold: true,
                labelOverride: i18next.t("Reporting:CbaExpensesAndRevenue.GrandTotalLabel"),
            };
        }
        return {};
    };

    return {
        title, path, id: tableId, initialSortBy, filterBarDef,
        onBeforeLoad, columnOverrides, parameters, defaultReportHierarchy, defaultReportVariants,
        rowFormatter,
        configListItemsOverride: cbaExpensesAndRevenueConfigListItemsOverride,
        disableAggregateButton: true,
    };
};

setReportDef(ReportId.CbaExpensesAndRevenue, getDefinition);
